import { type AnimationEvent, type CSSProperties, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Portal } from '@air/primitive-portal';
import { resetSelectedItemsAction } from '@air/redux-selected-items';
import { cn } from '@air/utils-tailwind';

import { useIsWorkspaceAgentPanelAvailable } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useIsWorkspaceAgentPanelAvailable';
import { usePrivateWorkspaceAgentPanelSessionStatus } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentPanelSessionStatus';
import { useWorkspaceAgentCreditsDepleted } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentCreditsDepleted';
import {
  isUploadedBatchOfferVisible,
  UPLOADED_BATCH_OFFER_VISIBLE_MS,
} from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/isUploadedBatchOfferVisible';
import { pickUploadedBatchOfferBatch } from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/pickUploadedBatchOfferBatch';
import {
  UPLOADED_BATCH_OFFER_PILL_LABEL,
  UploadedBatchOfferPill,
} from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/UploadedBatchOfferPill';
import styles from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/UploadedBatchOfferPill.module.css';
import { usePrivateFinishedImportBatch } from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/usePrivateFinishedImportBatch';
import { usePrivateFinishedUploadBatch } from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/usePrivateFinishedUploadBatch';
import {
  type UploadedBatchSource,
  useUploadedBatchOfferStore,
} from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/useUploadedBatchOfferStore';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { type MainSideNavOverlayId } from '~/components/v2/store/features/workspace/slices/createMainSideNavOverlaySlice';
import { useMainSideNavStore } from '~/components/v2/store/features/workspace/useMainSideNavStore';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useURLClipIdSelector } from '~/hooks/useURLClipIdSelector';

/** Matches `animation-duration-300` on the tray close in `MainSideNavOverlayTray`. */
const TRAY_CLOSE_ANIMATION_MS = 300;

/** Matches `.pushOut` in `UploadedBatchOfferPill.module.css`. */
const UPLOADED_BATCH_OFFER_PILL_PUSH_OUT_MS = 420;

/** Matches `.collapseIntoIcon` in `UploadedBatchOfferPill.module.css`. */
const UPLOADED_BATCH_OFFER_PILL_COLLAPSE_MS = 480;

const UPLOADED_BATCH_OFFER_PILL_GAP_PX = 6;

/** The rail item the pill slides out of, which is also the tray that hides it while open. */
const OFFER_TRAY_BY_SOURCE = {
  import: 'imports',
  upload: 'uploads',
} as const satisfies Record<UploadedBatchSource, MainSideNavOverlayId>;

const OFFER_TRAY_IDS: ReadonlySet<MainSideNavOverlayId | null> = new Set(Object.values(OFFER_TRAY_BY_SOURCE));

type UploadedBatchOfferAnchorPoint = {
  collapseX: number;
  left: number;
  top: number;
};

type ClosingTray = {
  trayId: MainSideNavOverlayId;
};

type UploadedBatchOfferPhase = 'closed' | 'entering' | 'open' | 'exiting';

type UploadedBatchOfferPillMotionStyle = CSSProperties & {
  '--pill-collapse-x'?: string;
};

const readUploadedBatchOfferAnchor = (trayId: MainSideNavOverlayId): UploadedBatchOfferAnchorPoint | null => {
  const anchor = document.querySelector(`[data-item-id="${trayId}"] [data-finished-batch-offer-anchor]`);

  if (!(anchor instanceof HTMLElement)) {
    return null;
  }

  const rect = anchor.getBoundingClientRect();

  return {
    collapseX: -(rect.width / 2 + UPLOADED_BATCH_OFFER_PILL_GAP_PX),
    left: rect.right + UPLOADED_BATCH_OFFER_PILL_GAP_PX,
    top: rect.top + rect.height / 2,
  };
};

export const PrivateUploadedBatchOffer = memo(() => {
  const { ui2026 } = useTemporaryFeatureFlags();
  // Click clears the selection, so a page whose panel is only up for the selection bar would show
  // nothing after the thread opens. This flag is header chrome, not canvas, and above medium.
  const { isWorkspaceAgentPanelAvailable } = useIsWorkspaceAgentPanelAvailable();
  const { finishedUploadBatch } = usePrivateFinishedUploadBatch();
  const { finishedImportBatch } = usePrivateFinishedImportBatch();
  const resolvedBatchIds = useUploadedBatchOfferStore((state) => state.resolvedBatchIds);
  const attachBatch = useUploadedBatchOfferStore((state) => state.attachBatch);
  const resolveBatch = useUploadedBatchOfferStore((state) => state.resolveBatch);
  const clearAttachedBatch = useUploadedBatchOfferStore((state) => state.clearAttachedBatch);
  const settleAttachedBatchThumbnails = useUploadedBatchOfferStore((state) => state.settleAttachedBatchThumbnails);
  const activeOverlay = useMainSideNavStore((state) => state.activeOverlay);
  const { isIdle } = usePrivateWorkspaceAgentPanelSessionStatus();
  const { isCreditsDepleted } = useWorkspaceAgentCreditsDepleted();
  const dispatch = useDispatch();
  const previousOverlayRef = useRef(activeOverlay);
  const [closingTray, setClosingTray] = useState<ClosingTray | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [anchorPoint, setAnchorPoint] = useState<UploadedBatchOfferAnchorPoint | null>(null);
  const [heldPoint, setHeldPoint] = useState<UploadedBatchOfferAnchorPoint | null>(null);
  const [phase, setPhase] = useState<UploadedBatchOfferPhase>('closed');

  const isAssetViewOpen = Boolean(useURLClipIdSelector());

  const batch = pickUploadedBatchOfferBatch({
    importBatch: finishedImportBatch,
    now,
    resolvedBatchIds,
    uploadBatch: finishedUploadBatch,
  });
  const trayId = OFFER_TRAY_BY_SOURCE[batch?.source ?? 'upload'];
  const isOfferWindowOpen = isUploadedBatchOfferVisible({
    batch,
    isAgentIdle: isIdle,
    isAssetViewOpen,
    isCreditsDepleted,
    isTrayClosing: closingTray?.trayId === trayId,
    isTrayOpen: activeOverlay === trayId,
    now,
    resolvedBatchIds,
  });
  const isVisible = Boolean(ui2026) && isWorkspaceAgentPanelAvailable && isOfferWindowOpen;

  useEffect(() => {
    return () => {
      clearAttachedBatch();
    };
  }, [clearAttachedBatch]);

  useEffect(() => {
    if (finishedImportBatch) {
      settleAttachedBatchThumbnails(finishedImportBatch);
    }
  }, [settleAttachedBatchThumbnails, finishedImportBatch]);

  useEffect(() => {
    const previousOverlay = previousOverlayRef.current;
    previousOverlayRef.current = activeOverlay;

    if (previousOverlay === activeOverlay || !previousOverlay || !OFFER_TRAY_IDS.has(previousOverlay)) {
      return;
    }

    // A fresh object per close, so closing the same tray again restarts the grace timer.
    setClosingTray({ trayId: previousOverlay });
  }, [activeOverlay]);

  // Its own effect, so opening another overlay mid-close does not cut the grace short.
  useEffect(() => {
    if (!closingTray) {
      return;
    }

    const timeout = window.setTimeout(() => setClosingTray(null), TRAY_CLOSE_ANIMATION_MS);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [closingTray]);

  useEffect(() => {
    if (!batch) {
      return;
    }

    const hideAt = batch.finishedAt + UPLOADED_BATCH_OFFER_VISIBLE_MS;
    const remaining = hideAt - Date.now();

    const showAsElapsed = () => {
      setNow((current) => {
        const next = Math.max(current, hideAt);

        return next === current ? current : next;
      });
    };

    if (remaining <= 0) {
      showAsElapsed();
      return;
    }

    const timeout = window.setTimeout(showAsElapsed, remaining);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [batch]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const measure = () => {
      setAnchorPoint(readUploadedBatchOfferAnchor(trayId));
    };

    measure();
    window.addEventListener('resize', measure);

    return () => {
      window.removeEventListener('resize', measure);
    };
  }, [isVisible, trayId]);

  useEffect(() => {
    if (isVisible && anchorPoint) {
      setHeldPoint(anchorPoint);
      setPhase((current) => (current === 'closed' || current === 'exiting' ? 'entering' : current));
      return;
    }

    if (isAssetViewOpen) {
      setHeldPoint(null);
      setPhase('closed');
      return;
    }

    setPhase((current) => (current === 'entering' || current === 'open' ? 'exiting' : current));
  }, [anchorPoint, isAssetViewOpen, isVisible]);

  useEffect(() => {
    if (phase !== 'entering') {
      return;
    }

    const timeout = window.setTimeout(() => {
      setPhase((current) => (current === 'entering' ? 'open' : current));
    }, UPLOADED_BATCH_OFFER_PILL_PUSH_OUT_MS);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== 'exiting') {
      return;
    }

    const timeout = window.setTimeout(() => {
      setHeldPoint(null);
      setPhase('closed');
    }, UPLOADED_BATCH_OFFER_PILL_COLLAPSE_MS);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [phase]);

  const handleMotionAnimationEnd = useCallback(
    (event: AnimationEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) {
        return;
      }

      if (phase === 'exiting') {
        setHeldPoint(null);
        setPhase('closed');
        return;
      }

      if (phase === 'entering') {
        setPhase('open');
      }
    },
    [phase],
  );

  const handleDismiss = useCallback(() => {
    if (!batch) {
      return;
    }

    resolveBatch(batch.batchId);
  }, [batch, resolveBatch]);

  const handleClick = useCallback(() => {
    if (!batch) {
      return;
    }

    attachBatch(batch);
    // Over an asset view the viewed clip would re-hydrate into the emptied selection and drop the batch.
    useWorkspaceAgentPanelStore.getState().optOutComposerAssetViewScope();
    dispatch(resetSelectedItemsAction());
    useViewsSelectAllStore.getState().resetSelectAll();
    useWorkspaceAgentPanelStore
      .getState()
      .startNewThread(batch.source === 'import' ? 'imports_offer' : 'uploads_offer');
  }, [attachBatch, batch, dispatch]);

  // The asset modal has no z-index, so the portaled pill would paint over it for the collapse.
  if (!ui2026 || isAssetViewOpen || phase === 'closed' || !heldPoint) {
    return null;
  }

  const pillStyle: CSSProperties = {
    left: heldPoint.left,
    top: heldPoint.top,
  };
  const motionStyle: UploadedBatchOfferPillMotionStyle = {
    '--pill-collapse-x': `${heldPoint.collapseX}px`,
  };

  return (
    <Portal>
      <div className="fixed z-[220] -translate-y-1/2" style={pillStyle}>
        <div
          className={cn(phase === 'exiting' ? styles.collapseIntoIcon : styles.pushOut)}
          onAnimationEnd={handleMotionAnimationEnd}
          style={motionStyle}
        >
          <UploadedBatchOfferPill
            label={UPLOADED_BATCH_OFFER_PILL_LABEL}
            onClick={handleClick}
            onDismiss={handleDismiss}
            playMarkIntro={phase === 'open'}
          />
        </div>
      </div>
    </Portal>
  );
});

PrivateUploadedBatchOffer.displayName = 'PrivateUploadedBatchOffer';
